/**
 * 运营阈值。
 *
 * ★ 和功能开关同一套做法(见 features/registry):
 *   一个阈值只有同时具备四样东西才算数 ——
 *
 *   1. **一个默认值**,而且是现在代码里那个值(改成可配不该顺手改行为)
 *   2. **一句「调了会怎样」**。运营看到「派单超时分钟数」不知道该填多少,
 *      看到「调小了会在司机正常操作的窗口里疯狂闪红」就知道了
 *   3. **上下界**。这些数字没有一个会让程序崩溃,它们只会让业务变形:
 *      填 0 分钟的话每一单派出去的瞬间都是「异常」
 *   4. **后端真的读它**。只在界面上摆一个输入框的,是个摆设
 *
 * ★ 为什么是这几个:它们是**运营的判断**,不是工程参数。
 *   「派单后多久没人接算异常」是这家店的服务标准,不是代码的实现细节;
 *   而 `PIN_LIMIT`、`CLUSTER_ZOOM` 那些是渲染性能的边界,
 *   放到界面上只会让人去调一个他无从判断的数字。
 */

export interface ThresholdDef {
  key: string;
  label: string;
  /** 这个数字在管什么 */
  description: string;
  /** ★ 调错了会怎样 —— 这句比 label 重要 */
  whenWrong: string;
  unit: string;
  default: number;
  min: number;
  max: number;
}

export const THRESHOLDS: readonly ThresholdDef[] = [
  {
    key: 'maxDevices',
    label: '一个账号最多同时登录几台设备',
    description: '超过之后,最久没用过的那台会被挤下线',
    whenWrong:
      '调成 1 就回到了「一账号一设备」:手机和平板换着用会互相踢,而被踢的那端只看到莫名其妙的退出;调太大则账号借出去也看不出来',
    unit: '台',
    default: 5,
    min: 1,
    max: 20,
  },
  {
    key: 'geocodeRetries',
    label: '地址搜索主用服务重试几次',
    description: '主用的地址服务连着失败这么多次,才降级到备用服务',
    whenWrong:
      '调成 1 的话主用服务偶尔抖一下就整批走备用(备用通常不如主用准);调太大则每一单都要等满这么多次超时,客服在电话里干等',
    unit: '次',
    default: 3,
    min: 1,
    max: 5,
  },
  {
    key: 'acceptGraceMinutes',
    label: '派单后多久没接算异常',
    description: '超过这个时间还没司机接单,这一单会出现在「异常」里',
    whenWrong:
      '调太小会在司机正常操作的窗口里疯狂闪红(接单本身就要几分钟);调太大则等发现时客户已经在催了',
    unit: '分钟',
    default: 30,
    min: 5,
    max: 240,
  },
  {
    key: 'unassignedHours',
    label: '建单后多久还没派算积压',
    description: '超过这个时间还没派给任何人,这一单会出现在「异常」里',
    whenWrong: '调太小的话每天早上刚导进来的单全是「积压」,这一栏就没人看了',
    unit: '小时',
    default: 4,
    min: 1,
    max: 72,
  },
  {
    key: 'checkInEarlyMinutes',
    label: '班次开始前多久可以签到',
    description: '司机最早可以提前这么久签到上班',
    whenWrong: '调成 0 的话早到的人被挡在门外,只能在门口干等到整点',
    unit: '分钟',
    default: 30,
    min: 0,
    max: 240,
  },
  {
    key: 'routeStopMinutes',
    label: '每一单在门口停留多久',
    description: '算「预计几点到」时,每经过一单就加上这么多时间',
    /*
     * ★ 为什么必须有这一项:Google 给的是**纯行车时间**。
     *
     *   一条二十单的路线,按纯行车时间算出来的「预计 14:00 送完」会早得离谱 ——
     *   司机在每家门口都要停:找车位、拎货、上楼、等人开门、拍照。
     *   调度照着那个时间回客户电话,等于每一通都在报一个做不到的数。
     */
    whenWrong:
      '填 0 就是「到门口不下车」—— 预计时间会比实际早一大截,而调度会照着它回客户电话;填太大则每一单都显示得比实际晚,司机会被当成在磨工',
    unit: '分钟',
    default: 5,
    min: 0,
    max: 60,
  },
] as const;

export type ThresholdKey = (typeof THRESHOLDS)[number]['key'];

const BY_KEY = new Map(THRESHOLDS.map((t) => [t.key, t]));

/**
 * 取一个阈值。
 *
 * ★ 认不出的 key 抛错,不是悄悄给个 0。
 *   打错一个字母就让「派单超时」变成 0 分钟 —— 每一单派出去的瞬间
 *   都是异常,而代码看起来在正常读配置。
 */
export function thresholdOf(settings: unknown, key: ThresholdKey): number {
  const def = BY_KEY.get(key);
  if (!def) throw new Error(`未知的运营阈值:${key}`);
  const v = (settings as Record<string, unknown> | null)?.[key];
  if (typeof v !== 'number' || !Number.isFinite(v)) return def.default;
  // 存进去的值也要夹在界内 —— 库里的旧值可能来自更早的一版
  return Math.min(def.max, Math.max(def.min, v));
}

/** 合成一份完整的阈值表 —— 界面上要显示每一个 */
export function resolveThresholds(settings: unknown): Array<ThresholdDef & { value: number }> {
  return THRESHOLDS.map((t) => ({ ...t, value: thresholdOf(settings, t.key as ThresholdKey) }));
}

/** 过滤掉不认识的 key、夹住越界的值 —— 别让人往这里塞任意 JSON */
export function sanitizeThresholds(input: Record<string, unknown>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(input)) {
    const def = BY_KEY.get(k);
    if (!def || typeof v !== 'number' || !Number.isFinite(v)) continue;
    out[k] = Math.min(def.max, Math.max(def.min, Math.round(v)));
  }
  return out;
}
